/**
 * CampusProfilePage — V2 orchestrator for the Campus profile.
 *
 * Manages:
 *   - Session & permissions
 *   - Single polling cycle (600s) for campus details, notes, server status, integrations
 *   - Tab state (basic info, settings, notes, server info, integrations, migrating, geofence, zone designer)
 *   - Drawer/dialog state for Edit, Note, Integration, and Impersonate flows
 *   - Onboarding template download/upload with polling
 *   - Master file upload dialog with PapaParse validation, polling, and navigation to preview page
 *   - MigratingTab (self-contained — manages own DynamoDB + SQL data & migration submissions)
 *   - Geofence read-only map + Zone Designer (self-contained GeofenceTool wrapper)
 *   - Toast notifications
 *
 * Props:
 *   id — Campus ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import './campus-profile-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../../servers/profile/components/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import SettingsTab from './components/SettingsTab.jsx';
import NotesTab from './components/NotesTab.jsx';
import NoteDrawer from './components/NoteDrawer.jsx';
import ServerInfoTab from './components/ServerInfoTab.jsx';
import IntegrationsTab from './components/IntegrationsTab.jsx';
import IntegrationDrawer from './components/IntegrationDrawer.jsx';
import MigratingTab from './components/MigratingTab.jsx';
import AuditLogsTab from './components/AuditLogsTab.jsx';
import GeofenceTab from './components/GeofenceTab.jsx';
import ZoneDesignerTab from './components/ZoneDesignerTab.jsx';

import {
  getSession, getCampusId, getUserId, getSessionId, getRoleWeight,
} from '../../../../services/session.js';
import {
  fetchCampusProfile, fetchCorporations, fetchTimezones, fetchUserRoles,
  editCampusBasicInfo, saveCampusSettings, saveCareflowSettings,
  fetchCampusNotes, addCampusNote, editCampusNote, deleteCampusNotes,
  fetchServerStatus,
  fetchVendorIntegrations, fetchCampusVendors,
  addIntegration, editIntegration, toggleIntegration, deleteIntegration,
  apiIntOnboarding, apiIntOnboardUpload, fetchOnboardStatus,
  fetchGeofence, fetchZoneTypes,
  apiUploadMasterFile, fetchUploadStatus,
} from './api/campusProfileApi.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
  formatPhone,
  setRoleInfo,
  reloadSessionCorp,
  reloadSessionCampusItems,
} from '../../../../libs.jsx';

// ==========================================================================
// COMPONENT
// ==========================================================================

const CampusProfilePage = ({ id }) => {
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const campusId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);
  const myCampusId = useMemo(() => getCampusId(), []);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);
  const auditRights = useMemo(() => iAmHigherAuth(99, myRoleWeight), [myRoleWeight]);

  /** Session corporate ID — used to determine if server links are enabled */
  const sessionCorpId = useMemo(() => getSession()?.corp ?? null, []);

  const getSessionParams = useCallback(() => ({
    campusId: myCampusId,
    userId: getUserId(),
    clientId: getSessionId(),
  }), [myCampusId]);

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 6000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // --------------------------------------------------------------------------
  // DATA STATE
  // --------------------------------------------------------------------------
  const [campusData, setCampusData] = useState(null);
  const [corpData, setCorpData] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Phase 3 — Notes
  const [noteData, setNoteData] = useState([]);

  // Phase 4 — Server Info
  const [deviceData, setDeviceData] = useState([]);

  // Phase 5 — Integrations
  const [vendorData, setVendorData] = useState([]);
  const [integrationData, setIntegrationData] = useState([]);

  // Phase 7 — Geofence + Zone Designer
  const [geoData, setGeoData] = useState({});
  const [geoData2, setGeoData2] = useState([]);
  const [zoneTypeData, setZoneTypeData] = useState([]);
  const [geoRefreshKey, setGeoRefreshKey] = useState(0);

  // UI state
  const [activeTab, setActiveTab] = useState('basic');
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Note drawer state
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [noteDrawerMode, setNoteDrawerMode] = useState('add');
  const [noteDrawerItem, setNoteDrawerItem] = useState(null);

  // Integration drawer state
  const [intDrawerOpen, setIntDrawerOpen] = useState(false);
  const [intDrawerMode, setIntDrawerMode] = useState('add');
  const [intDrawerItem, setIntDrawerItem] = useState(null);

  // Integration confirmation dialogs
  const [activateDialogItem, setActivateDialogItem] = useState(null);
  const [deactivateDialogItem, setDeactivateDialogItem] = useState(null);
  const [deleteIntDialogItem, setDeleteIntDialogItem] = useState(null);

  // Onboarding template download state
  const [downloadingIndex, setDownloadingIndex] = useState(-1);

  // Onboarding upload dialog
  const [uploadDialogItem, setUploadDialogItem] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Master file upload dialog
  const [masterUploadOpen, setMasterUploadOpen] = useState(false);
  const [masterFile, setMasterFile] = useState(null);       // base64-encoded CSV
  const [masterFileName, setMasterFileName] = useState('');
  const [masterFileData, setMasterFileData] = useState([]);  // PapaParse result
  const [masterFileValid, setMasterFileValid] = useState(false);
  const [masterChecked, setMasterChecked] = useState(false);
  const [masterUploading, setMasterUploading] = useState(false);
  const masterPollRef = useRef(null);

  // Impersonate
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [impersonateRoleWeight, setImpersonateRoleWeight] = useState(myRoleWeight);
  const [impersonating, setImpersonating] = useState(false);

  // --------------------------------------------------------------------------
  // DATA LOADING
  // --------------------------------------------------------------------------

  /** Fetch campus data (runs on mount + every 600s) */
  const fetchCampusDataCycle = useCallback(async () => {
    const [campusResult, notesResult, serverResult, vendorResult, intResult, geoResult, zoneTypesResult] = await Promise.all([
      fetchCampusProfile(myCampusId, campusId),
      fetchCampusNotes(myCampusId, campusId),
      fetchServerStatus(campusId),
      fetchCampusVendors(campusId),
      fetchVendorIntegrations(campusId),
      fetchGeofence(campusId),
      fetchZoneTypes(campusId),
    ]);

    if (campusResult && campusResult.length > 0) {
      setCampusData(campusResult[0]);
    }
    setNoteData(notesResult || []);
    setDeviceData(serverResult || []);
    setVendorData(vendorResult || []);
    setIntegrationData(intResult || []);
    setGeoData(geoResult?.geo1 || {});
    setGeoData2(geoResult?.geo2 || []);
    setZoneTypeData(zoneTypesResult || []);
    setLoading(false);
  }, [myCampusId, campusId]);

  /** Fetch support data (corporations, timezones, roles) — once on mount */
  const fetchSupportData = useCallback(async () => {
    const [corpResult, tzResult, rolesResult] = await Promise.all([
      fetchCorporations(myCampusId),
      fetchTimezones(myCampusId),
      fetchUserRoles(myCampusId),
    ]);
    setCorpData(corpResult || []);
    setTimezones(tzResult || []);
    setUserRoles(rolesResult || []);
  }, [myCampusId]);

  useEffect(() => {
    fetchCampusDataCycle();
    fetchSupportData();
    const interval = setInterval(fetchCampusDataCycle, 600_000);
    return () => clearInterval(interval);
  }, [fetchCampusDataCycle, fetchSupportData]);

  // --------------------------------------------------------------------------
  // HANDLERS — Basic Info
  // --------------------------------------------------------------------------

  /** Save edited basic info */
  const handleBasicInfoSave = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();

    const payload = [{
      campus_id: campusId,
      corporate_id: formData.corporateId,
      campus_country: formData.country,
      campus_state: formData.state,
      campus_name: formData.campusName,
      campus_city: formData.city,
      campus_zip: formData.zipcode,
      campus_email: formData.email,
      campus_phone: formData.phone,
      timezone_id: formData.timezoneId,
      modified_by: session.userId,
      campus_street_address: formData.streetAddress,
    }];

    const oldPayload = [{
      campus_id: campusId,
      corporate_id: campusData?.corporate_id,
      campus_country: campusData?.campus_country,
      campus_state: campusData?.campus_state,
      campus_name: campusData?.campus_name,
      campus_city: campusData?.campus_city,
      campus_zip: campusData?.campus_zip,
      campus_email: campusData?.campus_email,
      campus_phone: campusData?.campus_phone,
      timezone_id: campusData?.timezone_id,
      campus_street_address: campusData?.campus_street_address,
    }];

    const result = await editCampusBasicInfo(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Campus information saved.');
      setBasicDrawerOpen(false);
      fetchCampusDataCycle();
      // Update session if editing user's own campus
      if (campusId === myCampusId) {
        reloadSessionCampusItems(campusId, ['campus_name'], [formData.campusName], false);
      }
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [campusId, myCampusId, campusData, getSessionParams, showToast, fetchCampusDataCycle]);

  // --------------------------------------------------------------------------
  // HANDLERS — Impersonate
  // --------------------------------------------------------------------------

  const handleConfirmImpersonate = useCallback(() => {
    const dData = getSession();
    if (!dData) {
      showToast('error', 'Session not found');
      return;
    }

    setImpersonating(true);
    const selectedWeight = parseInt(impersonateRoleWeight, 10);
    const selectedRole = (userRoles || []).find((r) => r.user_role_weight === selectedWeight);
    setRoleInfo(selectedWeight, dData, selectedRole?.user_role_name || '');
    reloadSessionCorp(campusData?.corporate_id, 0, dData.cognito, dData, campusId);
  }, [campusData, campusId, impersonateRoleWeight, userRoles, showToast]);

  // --------------------------------------------------------------------------
  // HANDLERS — Settings
  // --------------------------------------------------------------------------

  const handleSaveSettings = useCallback(async (groupKey, { payload, oldPayload, sessionKeys, sessionVals, reload }) => {
    setSaving(true);
    const session = getSessionParams();

    // Add modified_by for non-careflow groups
    if (groupKey !== 'careflow') {
      payload[0].modified_by = session.userId;
    }

    const result = groupKey === 'careflow'
      ? await saveCareflowSettings(payload, oldPayload, session)
      : await saveCampusSettings(payload, oldPayload, session);

    setSaving(false);

    const labels = { architecture: 'Platform', reporting: 'Reporting', output: 'Output feature', careflow: 'Careflow campus' };

    if (result && result[0]) {
      showToast('success', `${labels[groupKey] || 'Settings'} settings saved.`);
      fetchCampusDataCycle();
      // Update session items if editing user's own campus corporate
      if (sessionKeys?.length && campusId === myCampusId) {
        reloadSessionCampusItems(campusId, sessionKeys, sessionVals, !!reload);
      }
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [getSessionParams, showToast, fetchCampusDataCycle, campusId, myCampusId]);

  // --------------------------------------------------------------------------
  // HANDLERS — Notes (Phase 3)
  // --------------------------------------------------------------------------

  /** Open Add Note drawer */
  const handleOpenAddNote = useCallback(() => {
    setNoteDrawerMode('add');
    setNoteDrawerItem(null);
    setNoteDrawerOpen(true);
  }, []);

  /** Open Edit Note drawer */
  const handleOpenEditNote = useCallback((noteRecord) => {
    setNoteDrawerMode('edit');
    setNoteDrawerItem(noteRecord);
    setNoteDrawerOpen(true);
  }, []);

  /** Save note (add or edit) */
  const handleSaveNote = useCallback(async (noteText) => {
    setSaving(true);
    const session = getSessionParams();

    let result;
    if (noteDrawerMode === 'add') {
      const payload = [{ campus_id: campusId, note: noteText, modified_by: session.userId }];
      result = await addCampusNote(payload, session);
    } else {
      const payload = [{
        note_id: noteDrawerItem?.note_id,
        campus_id: campusId,
        note: noteText,
        modified_by: session.userId,
      }];
      result = await editCampusNote(payload, session);
    }

    setSaving(false);

    if (result && result[0]) {
      showToast('success', noteDrawerMode === 'add' ? 'Note added successfully.' : 'Note updated successfully.');
      setNoteDrawerOpen(false);
      fetchCampusDataCycle();
    } else {
      showToast('error', `Error during ${noteDrawerMode === 'add' ? 'add' : 'update'}${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [noteDrawerMode, noteDrawerItem, campusId, getSessionParams, showToast, fetchCampusDataCycle]);

  /** Delete notes (bulk) */
  const handleDeleteNotes = useCallback(async (noteIds) => {
    setSaving(true);
    const session = getSessionParams();

    const payload = noteIds.map((nid) => ({ note_id: nid, modified_by: session.userId }));
    const result = await deleteCampusNotes(payload, session);

    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Notes deleted.');
      fetchCampusDataCycle();
    } else {
      showToast('error', `Error during delete${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [getSessionParams, showToast, fetchCampusDataCycle]);

  // --------------------------------------------------------------------------
  // HANDLERS — Integrations (Phase 5)
  // --------------------------------------------------------------------------

  /** Open Add Integration drawer */
  const handleOpenAddIntegration = useCallback((vendorItem) => {
    setIntDrawerMode('add');
    setIntDrawerItem(vendorItem);
    setIntDrawerOpen(true);
  }, []);

  /** Open Edit Integration drawer */
  const handleOpenEditIntegration = useCallback((intItem) => {
    setIntDrawerMode('edit');
    setIntDrawerItem(intItem);
    setIntDrawerOpen(true);
  }, []);

  /** Save integration (add or edit) */
  const handleSaveIntegration = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();
    const item = intDrawerItem;

    let result;
    if (intDrawerMode === 'add') {
      const payload = [{
        vendor_id: item.vendor_id,
        credentials: formData.credentials,
        vendor_auth_type_id: item.vendor_auth_type_id,
        auth: formData.authToken,
        external_campus_id: formData.externalCampusId,
        external_company_id: formData.externalCompanyId,
        is_sending: formData.isSending,
        is_receiving: formData.isReceiving,
        is_receiving_user: formData.isReceivingUsers,
        campus_id: campusId,
      }];
      result = await addIntegration(payload, session);
    } else {
      const payload = [{
        map_id: item.map_id,
        vendor_auth_id: item.vendor_auth_id,
        campus_id: campusId,
        vendor_auth_type_id: item.vendor_auth_type_id,
        credentials: formData.credentials,
        auth: formData.authToken,
        external_campus_id: formData.externalCampusId,
        external_company_id: formData.externalCompanyId,
        is_sending: formData.isSending,
        is_receiving: formData.isReceiving,
        is_receiving_user: formData.isReceivingUsers,
      }];
      const oldPayload = [{
        vendor_auth_id: item.vendor_auth_id,
        credentials: item.credentials,
        auth: item.auth,
        external_campus_id: item.external_campus_id,
        external_company_id: item.external_company_id,
        is_sending: item.is_sending,
        is_receiving: item.is_receiving,
        is_receiving_user: item.is_receiving_users,
      }];
      result = await editIntegration(payload, oldPayload, session);
    }

    setSaving(false);

    if (result && result[0]) {
      showToast('success', intDrawerMode === 'add' ? 'Integration setup successful.' : 'Integration updated.');
      setIntDrawerOpen(false);
      fetchCampusDataCycle();
    } else {
      showToast('error', `Error during ${intDrawerMode === 'add' ? 'setup' : 'update'}${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [intDrawerMode, intDrawerItem, campusId, getSessionParams, showToast, fetchCampusDataCycle]);

  /** Activate an integration */
  const handleActivateIntegration = useCallback(async () => {
    if (!activateDialogItem) return;
    setSaving(true);
    const session = getSessionParams();
    const item = activateDialogItem;

    const payload = [{
      map_id: item.map_id,
      vendor_auth_id: item.vendor_auth_id,
      is_active: true,
      modified_by: session.userId,
      campus_id: campusId,
    }];
    const oldPayload = [{
      map_id: item.map_id,
      vendor_auth_id: item.vendor_auth_id,
      is_active: false,
      campus_id: campusId,
    }];

    const result = await toggleIntegration(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Integration activated.');
      setActivateDialogItem(null);
      fetchCampusDataCycle();
      // Reload session ehr_active if same corp
      const dData = getSession();
      if (dData?.corp?.toString() === campusData?.corporate_id?.toString()) {
        reloadSessionCampusItems(campusId, ['ehr_active'], [true], true);
      }
    } else {
      showToast('error', `Error during activation${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [activateDialogItem, campusId, campusData, getSessionParams, showToast, fetchCampusDataCycle]);

  /** Deactivate an integration */
  const handleDeactivateIntegration = useCallback(async () => {
    if (!deactivateDialogItem) return;
    setSaving(true);
    const session = getSessionParams();
    const item = deactivateDialogItem;

    const payload = [{
      map_id: item.map_id,
      vendor_auth_id: item.vendor_auth_id,
      is_active: false,
      modified_by: session.userId,
      campus_id: campusId,
    }];
    const oldPayload = [{
      map_id: item.map_id,
      vendor_auth_id: item.vendor_auth_id,
      is_active: true,
      campus_id: campusId,
    }];

    const result = await toggleIntegration(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Integration deactivated.');
      setDeactivateDialogItem(null);
      fetchCampusDataCycle();
      const dData = getSession();
      if (dData?.corp?.toString() === campusData?.corporate_id?.toString()) {
        reloadSessionCampusItems(campusId, ['ehr_active'], [false], true);
      }
    } else {
      showToast('error', `Error during deactivation${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [deactivateDialogItem, campusId, campusData, getSessionParams, showToast, fetchCampusDataCycle]);

  /** Delete an integration */
  const handleDeleteIntegration = useCallback(async () => {
    if (!deleteIntDialogItem) return;
    setSaving(true);
    const session = getSessionParams();
    const item = deleteIntDialogItem;

    const payload = [{
      vendor_auth_id: item.vendor_auth_id,
      vendor_auth_type_id: item.vendor_auth_type_id,
      map_id: item.map_id,
      campus_id: campusId,
    }];

    const result = await deleteIntegration(payload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Integration deleted.');
      setDeleteIntDialogItem(null);
      setIntDrawerOpen(false);
      fetchCampusDataCycle();
    } else {
      showToast('error', `Error during delete${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [deleteIntDialogItem, campusId, getSessionParams, showToast, fetchCampusDataCycle]);

  /** Delete integration from drawer — shows confirmation dialog */
  const handleDeleteFromDrawer = useCallback((item) => {
    setIntDrawerOpen(false);
    setDeleteIntDialogItem(item);
  }, []);

  // --------------------------------------------------------------------------
  // HANDLERS — Onboarding Template Download
  // --------------------------------------------------------------------------

  /** Download onboarding template: generate control id → create spreadsheet → poll → download */
  const handleDownloadTemplate = useCallback(async (item, index) => {
    setDownloadingIndex(index);
    const session = getSessionParams();

    try {
      // Step 1: Generate control ID
      const genResult = await apiIntOnboarding({
        action: 'generate_control_id',
        campusId,
        corporateId: campusData?.corporate_id,
        clientId: item.external_campus_id,
        dataType: 'all',
        userId: session.userId,
        vendorId: item.vendor_id,
        async: false,
      });

      if (!genResult || Array.isArray(genResult) || !genResult.file_id) {
        showToast('error', 'Error generating control ID.');
        setDownloadingIndex(-1);
        return;
      }

      const fileId = genResult.file_id;
      showToast('info', 'Spreadsheet being created, this may take up to a minute.');

      // Step 2: Create spreadsheet (async)
      await apiIntOnboarding({
        action: 'create_spreadsheet',
        campusId,
        corporateId: campusData?.corporate_id,
        clientId: item.external_campus_id,
        dataType: 'all',
        userId: session.userId,
        vendorId: item.vendor_id,
        fileId,
        async: true,
      });

      // Step 3: Poll for completion
      const pollForCompletion = () => new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes max (5s intervals)
        const timer = setInterval(async () => {
          attempts++;
          try {
            const statusResult = await fetchOnboardStatus(myCampusId, fileId);
            if (statusResult && statusResult.length > 0) {
              const status = statusResult[0]?.status;
              if (status === 'completed' || status === 'completed_with_errors') {
                clearInterval(timer);
                if (status === 'completed_with_errors') {
                  showToast('warning', `File created with warnings: ${statusResult[0]?.error_message || ''}`);
                } else {
                  showToast('success', 'File created successfully.');
                }
                resolve(statusResult[0]);
              } else if (status === 'failed') {
                clearInterval(timer);
                reject(new Error(statusResult[0]?.error_message || 'File creation failed.'));
              }
            }
            if (attempts >= maxAttempts) {
              clearInterval(timer);
              reject(new Error('Timed out waiting for file creation.'));
            }
          } catch (err) {
            // Continue polling on transient errors
          }
        }, 5000);
      });

      const completedRecord = await pollForCompletion();

      // Step 4: Get presigned URL
      const urlResult = await apiIntOnboarding({
        action: 'get_presigned_url',
        campusId,
        corporateId: campusData?.corporate_id,
        clientId: item.external_campus_id,
        dataType: 'all',
        userId: session.userId,
        vendorId: item.vendor_id,
        fileId: completedRecord.file_id,
        async: false,
      });

      if (!urlResult || Array.isArray(urlResult) || !urlResult.presigned_url) {
        showToast('error', 'Error fetching download URL.');
        setDownloadingIndex(-1);
        return;
      }

      // Step 5: Download the file
      const resp = await fetch(urlResult.presigned_url);
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'onboarding-template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error('[handleDownloadTemplate] error:', err);
      showToast('error', err.message || 'Failed to download onboarding template.');
    } finally {
      setDownloadingIndex(-1);
    }
  }, [campusId, campusData, myCampusId, getSessionParams, showToast]);

  // --------------------------------------------------------------------------
  // HANDLERS — Onboarding Template Upload
  // --------------------------------------------------------------------------

  /** Open upload dialog */
  const handleOpenUploadDialog = useCallback((item) => {
    setUploadDialogItem(item);
    setUploadFile(null);
    setUploading(false);
  }, []);

  /** Execute onboard file upload */
  const handleUploadTemplate = useCallback(async () => {
    if (!uploadDialogItem || !uploadFile) return;
    setUploading(true);
    const session = getSessionParams();
    const item = uploadDialogItem;

    try {
      // Step 1: Get upload URL
      const urlResult = await apiIntOnboardUpload({
        action: 'get_upload_url',
        campusId,
        corporateId: campusData?.corporate_id,
        type: 'EHR',
        dataType: 'all',
        userId: session.userId,
        vendorId: item.vendor_id,
        async: false,
      });

      if (!urlResult?.presigned_upload_url) {
        showToast('error', `Error getting upload URL: ${urlResult?.message || 'Unknown error'}`);
        setUploading(false);
        return;
      }

      // Step 2: Upload file to S3
      const uploadResp = await fetch(urlResult.presigned_upload_url, {
        method: 'PUT',
        body: uploadFile,
        headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });

      if (!uploadResp.ok) {
        throw new Error(`Upload failed: ${uploadResp.status}`);
      }

      if (!urlResult.upload_id) {
        throw new Error('No upload_id returned from upload URL.');
      }

      // Step 3: Process upload (async)
      await apiIntOnboardUpload({
        action: 'process_upload',
        campusId,
        corporateId: campusData?.corporate_id,
        type: 'EHR',
        dataType: 'all',
        userId: session.userId,
        vendorId: item.vendor_id,
        uploadId: urlResult.upload_id,
        s3Bucket: urlResult.s3_bucket,
        s3Key: urlResult.s3_key,
        async: true,
      });

      showToast('info', 'File upload processing, this may take several minutes.');

      // Step 4: Poll for completion
      const pollUpload = () => new Promise((resolve, reject) => {
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes max (5s intervals)
        const timer = setInterval(async () => {
          attempts++;
          try {
            const statusResult = await fetchOnboardStatus(myCampusId, urlResult.upload_id);
            if (statusResult && statusResult.length > 0) {
              const status = statusResult[0]?.status;
              if (status === 'completed' || status === 'completed_with_errors') {
                clearInterval(timer);
                resolve(statusResult[0]);
              } else if (status === 'failed') {
                clearInterval(timer);
                reject(new Error(statusResult[0]?.error_message || 'Upload processing failed.'));
              }
            }
            if (attempts >= maxAttempts) {
              clearInterval(timer);
              reject(new Error('Timed out waiting for upload processing.'));
            }
          } catch (err) {
            // Continue polling
          }
        }, 5000);
      });

      const uploadResult = await pollUpload();
      if (uploadResult.status === 'completed_with_errors') {
        showToast('warning', `Upload successful with warnings: ${uploadResult.error_message || ''}`);
      } else {
        showToast('success', 'Upload successful.');
      }

      setUploadDialogItem(null);
      setUploadFile(null);
      fetchCampusDataCycle();
    } catch (err) {
      console.error('[handleUploadTemplate] error:', err);
      showToast('error', err.message || 'Failed to upload onboarding template.');
    } finally {
      setUploading(false);
    }
  }, [uploadDialogItem, uploadFile, campusId, campusData, myCampusId, getSessionParams, showToast, fetchCampusDataCycle]);

  // --------------------------------------------------------------------------
  // HANDLERS — Zone Designer refresh (Phase 7)
  // --------------------------------------------------------------------------

  /**
   * Refresh callback for GeofenceTool — called after zone save completes.
   * fetchGeofence always uses readOnly: false so fresh data is returned.
   * After fresh data arrives, bump geoRefreshKey to force GeofenceTool to
   * remount with the new zoneData (matches V1's re-creation pattern).
   */
  const handleGeoRefresh = useCallback(async () => {
    await fetchCampusDataCycle();
    setGeoRefreshKey(prev => prev + 1);
  }, [fetchCampusDataCycle]);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------

  /** Filtered role options for impersonate dialog */
  const filteredRoles = useMemo(() => {
    return (userRoles || [])
      .filter((r) => iAmHigherAuth(r.user_role_weight, myRoleWeight))
      .sort((a, b) => (a.user_role_weight || 0) - (b.user_role_weight || 0));
  }, [userRoles, myRoleWeight]);

  const campusName = campusData?.campus_name || '';
  const corporateName = campusData?.corporate_name || '';
  const corporateId = campusData?.corporate_id;

  /** Resolve timezone display name from timezones list */
  const timezoneName = useMemo(() => {
    if (!campusData?.timezone_id || !timezones?.length) return campusData?.timezone || '';
    const tz = timezones.find((t) => t.timezone_id === campusData.timezone_id);
    return tz ? `${tz.timezone_name} (${tz.timezone})` : campusData?.timezone || '';
  }, [campusData, timezones]);

  const bannerMeta = useMemo(() => [
    { label: 'Email', value: campusData?.campus_email || '\u2014' },
    { label: 'Phone', value: campusData?.campus_phone ? formatPhone(campusData.campus_phone) : '\u2014' },
    {
      label: 'Location',
      value: [campusData?.campus_city, campusData?.campus_state].filter(Boolean).join(', ') || '\u2014',
    },
  ], [campusData]);

  /** Timezone offset for note timestamps */
  const tzOffset = useMemo(() => {
    const dData = getSession();
    return parseInt(dData?.campusData?.[campusId]?.tz_offset ?? 0, 10);
  }, [campusId]);

  /**
   * Integration merge logic — mirrors V1 Campus.jsx lines 487-506.
   *
   * vendorData (getcampusvendors):  configured vendor integrations (credentials, auth, status, etc.)
   * integrationData (getvendorintegrations): vendor catalog / available vendors (logo, name, features, etc.)
   *
   * yesIntegrations = vendorData items whose vendor_id appears in integrationData,
   *                   enriched with catalog display fields (logo_file, vendor_name, website_url)
   * noIntegrations = integrationData items whose vendor_id NOT in vendorData
   */
  const [yesIntegrations, noIntegrations] = useMemo(() => {
    const matched = new Set();
    const yTmp = [];

    for (let i = 0; i < vendorData.length; i++) {
      for (let j = 0; j < integrationData.length; j++) {
        if (vendorData[i].vendor_id === integrationData[j].vendor_id) {
          matched.add(vendorData[i].vendor_id);
          const catalogItem = integrationData[j];
          const configuredItem = vendorData[i];
          yTmp.push({
            ...catalogItem,
            ...configuredItem,
            // Ensure vendor display fields fall back to catalog if missing from configured
            logo_file: configuredItem.logo_file || catalogItem.logo_file,
            vendor_name: configuredItem.vendor_name || catalogItem.vendor_name,
            website_url: configuredItem.website_url || catalogItem.website_url,
          });
        }
      }
    }

    const nTmp = integrationData.filter((item) => !matched.has(item.vendor_id));

    return [yTmp, nTmp];
  }, [vendorData, integrationData]);

  // --------------------------------------------------------------------------
  // HANDLERS — Master File Upload
  // --------------------------------------------------------------------------

  // Cleanup master upload polling on unmount
  useEffect(() => () => { if (masterPollRef.current) clearInterval(masterPollRef.current); }, []);

  /** Handle file selection — parse CSV with PapaParse */
  const handleMasterFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setMasterFileName(file.name);
    setMasterFileValid(false);
    setMasterChecked(false);

    // Read CSV text for validation
    const textReader = new FileReader();
    textReader.onload = (evt) => {
      const csvText = evt.target?.result || '';
      const parsed = Papa.parse(csvText, { header: true });
      setMasterFileData(parsed.data || []);
    };
    textReader.readAsText(file);

    // Read file as base64 for upload
    const b64Reader = new FileReader();
    b64Reader.onload = (evt) => {
      const dataUrl = evt.target?.result || '';
      // V1 extracts second CSV column from first row of the data URL parse — this is the base64 portion
      const parsed = Papa.parse(dataUrl);
      setMasterFile(parsed.data?.[0]?.[1] || '');
    };
    b64Reader.readAsDataURL(file);
  }, []);

  /** Validate parsed CSV data (matches V1 uploadPrecheck logic) */
  const handleMasterPrecheck = useCallback(() => {
    const charTest = /[/\\,."]/;
    let error = false;

    if (masterFileData.length <= 0) {
      showToast('error', 'File empty.');
      error = true;
    }

    if (!error) {
      for (let i = 0; i < masterFileData.length; i++) {
        const row = masterFileData[i];
        const rowNum = i + 2;

        // Area validation
        if (row?.room !== undefined && row.room !== '') {
          if (row?.areatype === undefined || row.areatype === '') {
            showToast('error', `Missing areatype for row ${rowNum}`);
            error = true; break;
          } else if (!['other', 'access', 'mc', 'al', 'il', 'snf', 'common'].includes(row.areatype?.toLowerCase())) {
            showToast('error', `Invalid areatype for row ${rowNum}`);
            error = true; break;
          } else if (charTest.test(row.room)) {
            showToast('error', `Erroneous character in room for row ${rowNum}`);
            error = true; break;
          }
        }

        // Resident validation
        if (row?.firstname !== undefined && row.firstname !== '') {
          if (row?.lastname === undefined || row.lastname === '') {
            showToast('error', `Missing lastname for row ${rowNum}`);
            error = true; break;
          } else if (charTest.test(row.firstname)) {
            showToast('error', `Erroneous character in firstname for row ${rowNum}`);
            error = true; break;
          } else if (charTest.test(row.lastname)) {
            showToast('error', `Erroneous character in lastname for row ${rowNum}`);
            error = true; break;
          }
        }
        if (row?.lastname !== undefined && row.lastname !== '') {
          if (row?.firstname === undefined || row.firstname === '') {
            showToast('error', `Missing firstname for row ${rowNum}`);
            error = true; break;
          } else if (charTest.test(row.lastname)) {
            showToast('error', `Erroneous character in lastname for row ${rowNum}`);
            error = true; break;
          } else if (charTest.test(row.firstname)) {
            showToast('error', `Erroneous character in firstname for row ${rowNum}`);
            error = true; break;
          }
        }

        // Device validation
        if (row?.deviceuse !== undefined && row.deviceuse !== '') {
          if (row?.serial === undefined || row.serial === '') {
            showToast('error', `Missing serial for row ${rowNum}`);
            error = true; break;
          }
        }
        if (row?.serial !== undefined && row.serial !== '') {
          if (row?.deviceuse === undefined || row.deviceuse === '') {
            showToast('error', `Missing deviceuse for row ${rowNum}`);
            error = true; break;
          }
        }
      }
    }

    setMasterChecked(true);
    if (!error) {
      setMasterFileValid(true);
      showToast('success', 'File good, ready for upload.');
    }
  }, [masterFileData, showToast]);

  /** Submit master file upload — sends to backend, polls for completion, then navigates to preview */
  const handleMasterUpload = useCallback(async () => {
    if (!editRights) {
      showToast('warning', 'You do not have permissions to perform this action.');
      return;
    }

    setMasterUploading(true);
    const userId = getUserId();

    try {
      const data = await apiUploadMasterFile({
        uploadFile: masterFile,
        campusId,
        userId,
      });

      if (data && (!Array.isArray(data) || data.length > 0)) {
        showToast('info', 'File upload in process, awaiting results...');
        const uploadKey = data?.Content;

        // Poll every 5s for upload_complete / upload_error
        let elapsed = 0;
        masterPollRef.current = setInterval(async () => {
          elapsed += 5;
          try {
            const statusRows = await fetchUploadStatus(uploadKey);
            if (statusRows && statusRows.length > 0) {
              if (statusRows[0]?.upload_complete === 1) {
                clearInterval(masterPollRef.current);
                masterPollRef.current = null;
                setMasterUploadOpen(false);
                setMasterUploading(false);
                navigate(`/profiles/campus/${campusId}/${uploadKey}`, {
                  state: { message: { success: 'Upload data staged for preview.', error: '' } },
                });
                return;
              }
              if (statusRows[0]?.upload_error === 1) {
                clearInterval(masterPollRef.current);
                masterPollRef.current = null;
                showToast('error', 'Error encountered. Run the file pre-check. If OK, please contact software support.');
                setMasterUploading(false);
                return;
              }
            }
          } catch {
            // continue polling on transient errors
          }

          if (elapsed >= 120) {
            clearInterval(masterPollRef.current);
            masterPollRef.current = null;
            showToast('error', 'Timed out performing upload. Please try again.');
            setMasterUploading(false);
          }
        }, 5000);
      } else {
        showToast('error', `Error during file upload${data?.[1] ? ': ' + data[1] : ''}.`);
        setMasterUploading(false);
      }
    } catch (err) {
      console.error('[CampusProfilePage] master upload error:', err);
      showToast('error', 'Upload request failed.');
      setMasterUploading(false);
    }
  }, [editRights, masterFile, campusId, navigate, showToast]);

  // --------------------------------------------------------------------------
  // TAB LIST
  // --------------------------------------------------------------------------
  const campusGuid = campusData?.campus_guid || '';

  const tabs = useMemo(() => {
    const list = [
      { key: 'basic', label: 'Basic Info' },
      { key: 'settings', label: 'Settings' },
      { key: 'integrations', label: 'Integrations' },
      { key: 'notes', label: 'Notes' },
      { key: 'server', label: 'Server Info' },
      { key: 'geofence', label: 'Geofence' },
      { key: 'designer', label: 'Zone Designer' },
    ];
    const isCloud = campusData?.is_cloud === 1 || campusData?.is_cloud === true;
    if (editRights && !isCloud) {
      list.push({ key: 'migrate', label: 'Migrating' });
    }
    if (auditRights) {
      list.push({ key: 'audit', label: 'Audit Logs' });
    }
    return list;
  }, [editRights, auditRights, campusData?.is_cloud]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="dp-page">
        {/* Toast */}
        {toast && (
          <div className={`dp-toast dp-toast--${toast.type}`}>
            <span>{toast.message}</span>
            <button className="dp-toast__close" onClick={() => setToast(null)}>{'\u2715'}</button>
          </div>
        )}

        {/* Page Header */}
        <header className="dp-page-header">
          <div className="dp-page-header__inner">
            <div className="dp-breadcrumb-row">
              <nav className="dp-breadcrumb">
                <Link to="/customers/campuses" className="dp-breadcrumb__text">Campuses</Link>
                <span className="dp-breadcrumb__separator">/</span>
                <span className="dp-breadcrumb__current">{campusName || 'Campus Profile'}</span>
              </nav>
              <div className="cpp-header-actions">
                {editRights && (
                  <button
                    type="button"
                    className="dp-btn dp-btn--secondary dp-btn--sm"
                    onClick={() => {
                      setMasterUploadOpen(true);
                      setMasterFile(null);
                      setMasterFileName('');
                      setMasterFileData([]);
                      setMasterFileValid(false);
                      setMasterChecked(false);
                    }}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                    Upload Masterfile
                  </button>
                )}
                {editRights && (
                  <button
                    type="button"
                    className="dp-btn dp-btn--secondary dp-btn--sm"
                    onClick={() => {
                      setImpersonateOpen(true);
                      setImpersonateRoleWeight(myRoleWeight);
                      setImpersonating(false);
                    }}
                  >
                    <svg className="dp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Impersonate
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={campusName}
          subtitle={`Campus ID #${campusId}`}
          metaFields={bannerMeta}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {activeTab === 'basic' && (
            <BasicInfoTab
              campusData={campusData}
              corporateName={corporateName}
              corporateId={corporateId}
              timezoneName={timezoneName}
              editRights={editRights}
              onEdit={() => setBasicDrawerOpen(true)}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab
              campusData={campusData}
              editRights={editRights}
              onSave={handleSaveSettings}
              saving={saving}
              showToast={showToast}
            />
          )}
          {activeTab === 'integrations' && (
            <IntegrationsTab
              yesIntegrations={yesIntegrations}
              noIntegrations={noIntegrations}
              editRights={editRights}
              onAdd={handleOpenAddIntegration}
              onEdit={handleOpenEditIntegration}
              onActivate={(item) => setActivateDialogItem(item)}
              onDeactivate={(item) => setDeactivateDialogItem(item)}
              onDelete={(item) => setDeleteIntDialogItem(item)}
              onDownloadTemplate={handleDownloadTemplate}
              onUploadTemplate={handleOpenUploadDialog}
              saving={saving}
              downloadingIndex={downloadingIndex}
            />
          )}
          {activeTab === 'notes' && (
            <NotesTab
              noteData={noteData}
              editRights={editRights}
              addRights={editRights}
              onAddNote={handleOpenAddNote}
              onEditNote={handleOpenEditNote}
              onDeleteNotes={handleDeleteNotes}
              tzOffset={tzOffset}
            />
          )}
          {activeTab === 'server' && (
            <ServerInfoTab
              deviceData={deviceData}
              campusId={campusId}
              corporateId={corporateId}
              sessionCorpId={sessionCorpId}
              showToast={showToast}
            />
          )}
          {activeTab === 'geofence' && (
            <GeofenceTab
              geoData={geoData}
              latitude={campusData?.campus_latitude}
              longitude={campusData?.campus_longitude}
            />
          )}
          {activeTab === 'designer' && (
            <ZoneDesignerTab
              key={geoRefreshKey}
              latitude={campusData?.campus_latitude}
              longitude={campusData?.campus_longitude}
              campusId={campusId}
              campusName={campusName}
              zoneData={geoData2}
              zoneTypes={zoneTypeData}
              onRefresh={handleGeoRefresh}
            />
          )}
          {activeTab === 'migrate' && editRights && (
            <MigratingTab
              campusId={campusId}
              campusGuid={campusGuid}
              editRights={editRights}
              showToast={showToast}
              getSessionParams={getSessionParams}
            />
          )}
          {activeTab === 'audit' && auditRights && (
            <AuditLogsTab
              campusId={campusId}
              myCampusId={myCampusId}
            />
          )}
        </div>

        {/* Basic Info Drawer (Edit Campus) */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          campusData={campusData}
          corporations={corpData}
          timezones={timezones}
        />

        {/* Note Drawer (Add / Edit Note) */}
        {noteDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setNoteDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <NoteDrawer
          open={noteDrawerOpen}
          onClose={() => setNoteDrawerOpen(false)}
          onSave={handleSaveNote}
          saving={saving}
          mode={noteDrawerMode}
          noteData={noteDrawerItem}
        />

        {/* Integration Drawer (Add / Edit Integration) */}
        {intDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setIntDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <IntegrationDrawer
          open={intDrawerOpen}
          onClose={() => setIntDrawerOpen(false)}
          onSave={handleSaveIntegration}
          onDelete={handleDeleteFromDrawer}
          saving={saving}
          mode={intDrawerMode}
          vendorItem={intDrawerItem}
        />

        {/* Activate Integration Dialog */}
        {activateDialogItem && (
          <div className="pp-dialog-backdrop" onClick={() => setActivateDialogItem(null)}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Activate Integration">
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">Activate Integration</h2>
                <button className="dp-drawer__close" onClick={() => setActivateDialogItem(null)}>{'\u2715'}</button>
              </div>
              <div className="pp-dialog__body">
                <p style={{ margin: 0, lineHeight: 1.6 }}>
                  Are you sure you want to activate <strong>{activateDialogItem.vendor_name || 'this integration'}</strong>?
                </p>
              </div>
              <div className="pp-dialog__footer">
                <button className="dp-btn dp-btn--cancel" onClick={() => setActivateDialogItem(null)}>Cancel</button>
                <button className="dp-btn dp-btn--primary" onClick={handleActivateIntegration} disabled={saving}>
                  {saving ? 'Activating...' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deactivate Integration Dialog */}
        {deactivateDialogItem && (
          <div className="pp-dialog-backdrop" onClick={() => setDeactivateDialogItem(null)}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Deactivate Integration">
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">Deactivate Integration</h2>
                <button className="dp-drawer__close" onClick={() => setDeactivateDialogItem(null)}>{'\u2715'}</button>
              </div>
              <div className="pp-dialog__body">
                <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--color-danger, #c53030)' }}>
                  Are you sure you want to deactivate <strong>{deactivateDialogItem.vendor_name || 'this integration'}</strong>?
                </p>
              </div>
              <div className="pp-dialog__footer">
                <button className="dp-btn dp-btn--cancel" onClick={() => setDeactivateDialogItem(null)}>Cancel</button>
                <button className="dp-btn dp-btn--danger" onClick={handleDeactivateIntegration} disabled={saving}>
                  {saving ? 'Deactivating...' : 'De-activate'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Integration Dialog */}
        {deleteIntDialogItem && (
          <div className="pp-dialog-backdrop" onClick={() => setDeleteIntDialogItem(null)}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Delete Integration">
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">Delete Integration</h2>
                <button className="dp-drawer__close" onClick={() => setDeleteIntDialogItem(null)}>{'\u2715'}</button>
              </div>
              <div className="pp-dialog__body">
                <p style={{ margin: 0, lineHeight: 1.6, color: 'var(--color-danger, #c53030)' }}>
                  Are you sure you want to permanently delete <strong>{deleteIntDialogItem.vendor_name || 'this integration'}</strong>?
                </p>
                <p style={{ margin: 'var(--space-2, 0.5rem) 0 0', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                  This action cannot be undone.
                </p>
              </div>
              <div className="pp-dialog__footer">
                <button className="dp-btn dp-btn--cancel" onClick={() => setDeleteIntDialogItem(null)}>Cancel</button>
                <button className="dp-btn dp-btn--danger" onClick={handleDeleteIntegration} disabled={saving}>
                  {saving ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Template Dialog */}
        {uploadDialogItem && (
          <div className="pp-dialog-backdrop" onClick={() => { if (!uploading) setUploadDialogItem(null); }}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Upload Template">
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">Upload Onboarding Template</h2>
                <button className="dp-drawer__close" onClick={() => { if (!uploading) setUploadDialogItem(null); }}>{'\u2715'}</button>
              </div>
              <div className="pp-dialog__body">
                <p style={{ margin: '0 0 1rem', color: 'var(--color-text-secondary, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                  Upload an onboarding spreadsheet for <strong>{uploadDialogItem.vendor_name || 'this vendor'}</strong>.
                </p>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  disabled={uploading}
                  style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                />
              </div>
              <div className="pp-dialog__footer">
                <button
                  className="dp-btn dp-btn--cancel"
                  onClick={() => setUploadDialogItem(null)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  className="dp-btn dp-btn--primary"
                  onClick={handleUploadTemplate}
                  disabled={uploading || !uploadFile}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Master File Upload Dialog */}
        {masterUploadOpen && (
          <div className="pp-dialog-backdrop" onClick={() => { if (!masterUploading) setMasterUploadOpen(false); }}>
            <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Upload Master File">
              <div className="pp-dialog__header">
                <h2 className="pp-dialog__title">Upload Master File CSV</h2>
                <button className="dp-drawer__close" onClick={() => { if (!masterUploading) setMasterUploadOpen(false); }}>{'\u2715'}</button>
              </div>
              <div className="pp-dialog__body">
                <p style={{ margin: '0 0 1rem', color: 'var(--color-text-secondary, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                  Select a CSV file containing areas, residents, and/or devices to upload.
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleMasterFileChange}
                  disabled={masterUploading}
                  style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                />
                {masterFileName && (
                  <p style={{ margin: '0.75rem 0 0', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                    Selected: <strong>{masterFileName}</strong>
                  </p>
                )}
                {masterChecked && masterFileValid && (
                  <p style={{ margin: '0.5rem 0 0', fontSize: 'var(--font-size-sm, 0.875rem)', color: 'var(--color-success, #16a34a)', float: "right" }}>
                    {'\u2713'} File validated successfully
                  </p>
                )}
                {masterChecked && !masterFileValid && (
                  <p style={{ margin: '0.5rem 0 0', fontSize: 'var(--font-size-sm, 0.875rem)', color: 'var(--color-danger, #c53030)', float: "right" }}>
                    {'\u2717'} File has validation errors
                  </p>
                )}
              </div>
              <div className="pp-dialog__footer">
                <button
                  className="dp-btn dp-btn--cancel"
                  onClick={() => setMasterUploadOpen(false)}
                  disabled={masterUploading}
                >
                  Cancel
                </button>
                <button
                  className="dp-btn dp-btn--secondary"
                  onClick={handleMasterPrecheck}
                  disabled={!masterFile || masterUploading}
                >
                  Check File
                </button>
                <button
                  className="dp-btn dp-btn--primary"
                  onClick={handleMasterUpload}
                  disabled={!masterFileValid || masterUploading}
                >
                  {masterUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Impersonate Dialog */}
        {impersonateOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setImpersonateOpen(false)} aria-hidden="true">
            <div className="cpp-impersonate-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Impersonate">
              <h3 className="cpp-impersonate-dialog__title">Impersonate</h3>
              <p className="cpp-impersonate-dialog__campus">
                {campusName}
              </p>
              <div className="dp-drawer__field">
                <label htmlFor="cpp-impersonate-role" className="dp-drawer__label">
                  Impersonating User Role
                </label>
                <select
                  id="cpp-impersonate-role"
                  value={impersonateRoleWeight}
                  onChange={(e) => setImpersonateRoleWeight(e.target.value)}
                  className="dp-drawer__select"
                >
                  <option value={myRoleWeight}>My Role</option>
                  {filteredRoles.map((r) => (
                    <option key={r.user_role_weight} value={r.user_role_weight}>
                      {r.user_role_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="cpp-impersonate-dialog__actions">
                <button
                  type="button"
                  onClick={() => setImpersonateOpen(false)}
                  className="dp-btn dp-btn--secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmImpersonate}
                  disabled={impersonating}
                  className="dp-btn dp-btn--primary"
                >
                  {impersonating ? 'Switching...' : 'Impersonate'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

      </div>
      </div>
    </>
  );
};

export default CampusProfilePage;
